import React, { useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  Link as FluentLink,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Tooltip,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { Location24Regular, Map24Regular, BookmarkMultiple24Regular, Dismiss20Regular, Open16Regular } from '@fluentui/react-icons';
import LocationPicker from '../LocationPicker';
import LocationPreview from './LocationPreview';
import { SectionShell } from './SectionShell';

const useStyles = makeStyles({
  // Row holding the "Choose from saved locations" + "Pick on map" buttons.
  // Side-by-side on desktop; stacked vertically on mobile so each button gets
  // the full width instead of being squeezed onto one cramped line.
  pickerRow: {
    display: 'flex',
    ...shorthands.gap('12px'),
    '@media (max-width: 768px)': {
      flexDirection: 'column',
    },
  },
  locationCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.gap('12px'),
    minHeight: 'auto',
    ...shorthands.padding('12px', '16px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground1,
  },
  // The populated card doubles as the trigger that opens the read-only map
  // preview dialog — click anywhere on it (except the remove button).
  locationCardClickable: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    '&:focus-visible': {
      ...shorthands.outline('2px', 'solid', tokens.colorStrokeFocus2),
      outlineOffset: '2px',
    },
  },
  // Right-side actions on a populated card: the map-preview thumbnail and
  // (editable card only) the remove button.
  locationCardActions: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
    flexShrink: 0,
  },
  locationCardContent: {
    display: 'flex',
    ...shorthands.gap('12px'),
    alignItems: 'center',
    flexGrow: 1,
    minWidth: 0,
  },
  locationCardIcon: {
    fontSize: '20px',
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  locationCardLabel: {
    fontSize: '14px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    marginBottom: '4px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  locationCardText: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  locationCardCoords: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    fontFamily: 'monospace',
    marginTop: '2px',
  },
  locationCardEmpty: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('12px'),
    width: '100%',
    ...shorthands.padding('8px', '12px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground1,
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: 'inherit',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  locationCardEmptyLink: {
    fontSize: '14px',
    color: tokens.colorBrandForeground1,
  },
  manageRow: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('6px'),
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
  },
});

interface CustomerLocation {
  label: string;
  lat: number;
  lng: number;
}

interface LocationSectionProps {
  customerLocations: CustomerLocation[];
  selectedLocationIndex: number | null;
  onLocationIndexSelect: (index: number) => void;
  currentLocation: { lat: number; lng: number } | null;
  currentLocationAddress: string;
  currentLocationName?: string;
  onLocationPickerSelect: (loc: { lat: number; lng: number; address: string; name: string }) => void;
  hasCustomerSelected: boolean;
  onSaveLocationToCustomer: () => void;
  onAddLocationToOrder: () => void;
  /** Clears the order's delivery location entirely. Returns the section to
   *  the empty / "Pick on map" state. */
  onRemoveLocation?: () => void;
  showIcon?: boolean;
  /** Read-only mode: renders the card without the map picker / remove. Two
   *  cases — a project child order (location owned by the parent project; pass
   *  onManageClick for the link out to manage it there), or a plain edit-locked
   *  order. The empty-state copy keys on onManageClick to pick project vs. order
   *  wording. */
  readOnly?: boolean;
  manageLabel?: string;
  onManageClick?: () => void;
}

const formatCoords = (lat: number, lng: number) =>
  `${lat.toFixed(6)}, ${lng.toFixed(6)}`;

const LocationSectionBase: React.FC<LocationSectionProps> = ({
  customerLocations,
  selectedLocationIndex,
  onLocationIndexSelect,
  currentLocation,
  currentLocationAddress,
  currentLocationName,
  onLocationPickerSelect,
  hasCustomerSelected,
  onSaveLocationToCustomer,
  onAddLocationToOrder,
  onRemoveLocation,
  showIcon = true,
  readOnly = false,
  manageLabel,
  onManageClick,
}) => {
  const styles = useStyles();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [saveToCustomer, setSaveToCustomer] = useState(false);
  // Snapshot of the committed location at the time the picker opens. Used
  // both to suppress the location card during an uncommitted session and to
  // revert parent state if the user dismisses the dialog without clicking
  // "Add location" (the picker pushes per-pin emits to the parent as the
  // user explores).
  const priorLocationRef = useRef<
    | { lat: number; lng: number; address: string; name: string }
    | null
  >(null);
  const committedRef = useRef(false);

  const showPickerRow = !currentLocation || customerLocations.length > 0;
  const showLocationCard =
    currentLocation && (!dialogOpen || !!priorLocationRef.current);

  const openPicker = () => {
    priorLocationRef.current = currentLocation
      ? {
          lat: currentLocation.lat,
          lng: currentLocation.lng,
          address: currentLocationAddress || '',
          name: currentLocationName || '',
        }
      : null;
    committedRef.current = false;
    setDialogOpen(true);
  };

  const closePicker = () => {
    if (!committedRef.current) {
      if (priorLocationRef.current) {
        onLocationPickerSelect(priorLocationRef.current);
      } else if (onRemoveLocation) {
        onRemoveLocation();
      }
    }
    setSaveToCustomer(false);
    setDialogOpen(false);
  };

  const commitPicker = () => {
    committedRef.current = true;
    if (saveToCustomer && hasCustomerSelected) onSaveLocationToCustomer();
    onAddLocationToOrder();
    setSaveToCustomer(false);
    setDialogOpen(false);
  };

  // Read-only: the parent project owns the service location. Show the card (or
  // an empty hint) plus a link to manage it on the project — no picker, no
  // remove, no dialog.
  if (readOnly) {
    return (
      <SectionShell title="Location" icon={<Location24Regular />} showIcon={showIcon}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {currentLocation ? (
            <>
              <Tooltip content="Preview location on map" relationship="label">
                <div
                  className={mergeClasses(styles.locationCard, styles.locationCardClickable)}
                  role="button"
                  tabIndex={0}
                  onClick={() => setPreviewOpen(true)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setPreviewOpen(true);
                    }
                  }}
                >
                  <div className={styles.locationCardContent}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div className={styles.locationCardLabel}>
                        {currentLocationName?.trim() || 'Service Location'}
                      </div>
                      <div className={styles.locationCardText}>
                        {currentLocationAddress ||
                          formatCoords(currentLocation.lat, currentLocation.lng)}
                      </div>
                    </div>
                  </div>
                </div>
              </Tooltip>
              <LocationPreview
                lat={currentLocation.lat}
                lng={currentLocation.lng}
                name={currentLocationName}
                address={currentLocationAddress}
                open={previewOpen}
                onOpenChange={setPreviewOpen}
              />
            </>
          ) : (
            <div className={styles.locationCard} style={{ minHeight: 'auto' }}>
              <div className={styles.locationCardContent}>
                <Location24Regular className={styles.locationCardIcon} />
                <div className={styles.locationCardText}>
                  {onManageClick
                    ? 'No site location set on the project yet.'
                    : 'No site location set yet.'}
                </div>
              </div>
            </div>
          )}
          {onManageClick && (
            <FluentLink
              as="button"
              className={styles.manageRow}
              onClick={onManageClick}
            >
              {manageLabel || 'Manage on project'}
              <Open16Regular />
            </FluentLink>
          )}
        </div>
      </SectionShell>
    );
  }

  return (
    <SectionShell title="Location" icon={<Location24Regular />} showIcon={showIcon}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {showPickerRow && (
          <div className={styles.pickerRow}>
            {customerLocations.length > 0 && (
              <Menu>
                <MenuTrigger disableButtonEnhancement>
                  <button
                    type="button"
                    className={styles.locationCardEmpty}
                    style={{ flex: 1, width: 'auto' }}
                  >
                    <BookmarkMultiple24Regular className={styles.locationCardIcon} />
                    <span className={styles.locationCardEmptyLink}>
                      Choose from saved locations
                    </span>
                  </button>
                </MenuTrigger>
                <MenuPopover>
                  <MenuList>
                    {customerLocations.map((location, index) => {
                      const text =
                        location.label || formatCoords(location.lat, location.lng);
                      return (
                        <MenuItem
                          key={index}
                          onClick={() => onLocationIndexSelect(index)}
                        >
                          {text}
                        </MenuItem>
                      );
                    })}
                  </MenuList>
                </MenuPopover>
              </Menu>
            )}
            <button
              type="button"
              className={styles.locationCardEmpty}
              onClick={openPicker}
              style={{ flex: 1, width: 'auto' }}
            >
              <Map24Regular className={styles.locationCardIcon} />
              <span className={styles.locationCardEmptyLink}>Pick on map</span>
            </button>
          </div>
        )}
        {showLocationCard && (
          <>
            <Tooltip content="Preview location on map" relationship="label">
              <div
                className={mergeClasses(styles.locationCard, styles.locationCardClickable)}
                role="button"
                tabIndex={0}
                onClick={() => setPreviewOpen(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setPreviewOpen(true);
                  }
                }}
              >
                <div className={styles.locationCardContent}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div className={styles.locationCardLabel}>
                      {currentLocationName?.trim() || 'Service Location'}
                    </div>
                    <div className={styles.locationCardText}>
                      {currentLocationAddress ||
                        formatCoords(currentLocation.lat, currentLocation.lng)}
                    </div>
                  </div>
                </div>
                <div className={styles.locationCardActions}>
                  {onRemoveLocation && (
                    <Tooltip content="Remove location" relationship="label">
                      <Button
                        appearance="subtle"
                        icon={<Dismiss20Regular />}
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemoveLocation();
                        }}
                        aria-label="Remove location"
                        style={{ minWidth: 'auto' }}
                      />
                    </Tooltip>
                  )}
                </div>
              </div>
            </Tooltip>
            <LocationPreview
              lat={currentLocation.lat}
              lng={currentLocation.lng}
              name={currentLocationName}
              address={currentLocationAddress}
              open={previewOpen}
              onOpenChange={setPreviewOpen}
            />
          </>
        )}

        <LocationPicker
          size="large"
          mapInDialog
          defaultSatellite
          dialogOpen={dialogOpen}
          onDialogOpenChange={(open) => (open ? openPicker() : closePicker())}
          initialLocation={
            currentLocation
              ? {
                lat: currentLocation.lat,
                lng: currentLocation.lng,
                address:
                  currentLocationAddress ||
                  formatCoords(currentLocation.lat, currentLocation.lng),
                name: currentLocationName || undefined,
              }
              : null
          }
          onLocationSelect={onLocationPickerSelect}
          dialogActions={
            <>
              {hasCustomerSelected && (
                <Checkbox
                  checked={saveToCustomer}
                  onChange={(_e, data) => setSaveToCustomer(!!data.checked)}
                  label="Add to customer's saved locations"
                  style={{ marginRight: 'auto', whiteSpace: 'nowrap' }}
                />
              )}
              <Button
                appearance="secondary"
                onClick={closePicker}
                style={{ whiteSpace: 'nowrap' }}
              >
                Cancel
              </Button>
              {currentLocation ? (
                <Button
                  appearance="primary"
                  onClick={commitPicker}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  Add location
                </Button>
              ) : (
                <Tooltip
                  content="Right-click on the map (or search) to drop a pin first."
                  relationship="description"
                >
                  <Button
                    appearance="primary"
                    disabledFocusable
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    Add location
                  </Button>
                </Tooltip>
              )}
            </>
          }
        />
      </div>
    </SectionShell>
  );
};

export const LocationSection = React.memo(LocationSectionBase);
export default LocationSection;
